import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Button,
  Select,
  Divider,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import dayjs from "dayjs";

import KPTimePicker from "../../../components/KPTimePicker";
import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";
import ROUTER from "../../../constants/router";

import MultiFileManager from "../../File/MultiFileManager/MultiFileManager";
import DuplicateCheckModal from "../../../components/StudentGuidance/DuplicateCheckModal";

import { uploadMultiFile } from "../../../reducer/file/action";
import { updateStudentGuidance, checkDuplicateStudentGuidance } from "../../../reducer/studentGuidance/action";
import { statisticUnaccepted } from "../../../reducer/profile/action";
import { notification } from "../../../reducer/home/action";

import { getCurrentSchoolYear } from "../../User/TimeResearch/utils";
import { STUDENT_GUIDANCE } from "../../../constants/selectOptions";

function StudentGuidanceUpdateModal(props) {
  const { updating, setUpdating } = props;
  const [disabled, setDisabled] = useState(true);

  const currentYear = getCurrentSchoolYear();
  const [values, setValues] = useState(null);
  const [profileList, setProfileList] = useState([]);
  const [fileUrls, setFileUrls] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [fileManagerKey, setFileManagerKey] = useState(0);
  const [timeResearchYear, setTimeResearchYear] = useState(currentYear);
  const [timeResearchDetailList, setTimeResearchDetailList] = useState([]);
  const [helperText, setHelperText] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateList, setDuplicateList] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const profileListFromProps = props.profileList;

  useEffect(() => {
    if (updating) {
      resetValues();
      if (updating.year) {
        setTimeResearchYear(updating.year);
      }
    }
  }, [updating]);

  useEffect(() => {
    if (updating?._fromDuplicate) {
      setDisabled(false);
    }
  }, [updating]);

  useEffect(() => {
    if (updating?._fromDuplicate && props.profile?.id) {
      const isAlreadyMember = profileList.some((member) => member.profileId === props.profile.id);
      if (!isAlreadyMember) {
        setProfileList([
          ...profileList,
          {
            profileId: props.profile.id,
            role: "main",
            isInternal: true,
            externalName: "",
            timeAmount: null,
          },
        ]);
      }
    }
  }, [updating, props.profile]);

  useEffect(() => {
    if (props.timeResearchInformationList?.length > 0 && timeResearchYear) {
      let list = props.timeResearchInformationList
        ?.find((e) => e.year === 0)
        ?.timeResearchDetail.filter((e) => !e.allowedUpdateNum && e.type === "studentGuidance");
      setTimeResearchDetailList(list || []);
    }
  }, [props.timeResearchInformationList, timeResearchYear]);

  const resetValues = () => {
    // Convert completionDate from ISO/SQL format to dd/mm/yyyy format for KPTimePicker
    let completionDateFormatted = "dd/mm/yyyy";
    if (updating?.completionDate) {
      const dateObj = dayjs(updating.completionDate);
      if (dateObj.isValid()) {
        completionDateFormatted = dateObj.format("DD/MM/YYYY");
      }
    }

    setValues({
      ...updating,
      completionDate: completionDateFormatted,
    });
    setProfileList(Array.isArray(updating.studentGuidanceProfile) ? [...updating.studentGuidanceProfile] : []);
    setFileUrls(updating.fileUrl?.split("||").filter(Boolean) || []);
    setNewFiles([]);
    setFileManagerKey((prev) => prev + 1);
  };

  const handleChangeForm = (name, value) => {
    if (name === "timeResearchDetailCode") {
      setValues({
        ...values,
        [name]: value === "null" ? null : value,
      });
    } else if (name === "completionDate" && value) {
      // Auto-calculate year from completionDate
      // KPTimePicker returns dd/mm/yyyy format, need to parse it
      if (value !== "dd/mm/yyyy") {
        const parts = value.split("/");
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);

        // Calculate academic year: months 1-3 use previous year, months 4-12 use current year
        const academicYear = month >= 1 && month <= 3 ? year - 1 : year;

        setTimeResearchYear(academicYear);
        setValues({
          ...values,
          [name]: value,
          year: academicYear,
        });
        return;
      }
    } else {
      setValues({ ...values, [name]: value });
    }
  };

  const handleChangeProfileList = (index, name, value) => {
    const temp = profileList;
    const tempElm = profileList[index];
    temp.splice(index, 1, { ...tempElm, [name]: value });
    setProfileList([...temp]);
  };

  const handleInsert = () => {
    const temp = profileList;
    temp.push({
      profileId: null,
      roleInStudentGuidance: "co_instructor",
      isInternal: true,
      externalName: "",
    });
    setProfileList([...temp]);
  };

  const handleRemove = (index) => {
    const temp = profileList;
    temp.splice(index, 1);
    setProfileList([...temp]);
  };

  const handleClose = () => {
    setDuplicateModalOpen(false);
    setDuplicateList([]);
    setPendingSubmit(false);
    if (!disabled) {
      resetValues();
      setDisabled(true);
    } else {
      setUpdating(null);
      setDisabled(true);
      setFileUrls([]);
      setNewFiles([]);
      setFileManagerKey((prev) => prev + 1);
    }
  };

  // Convert dd/mm/yyyy to YYYY-MM-DDTHH:mm:ssZ for backend
  const convertDateToISO = (value) => {
    if (!value || value === "dd/mm/yyyy") return null;
    const dateForm = value.replaceAll("/", "");
    const date = dateForm.substr(0, 2);
    const month = dateForm.substr(2, 2);
    const year = dateForm.substr(-4);
    return dayjs(`${month}/${date}/${year}`).format("YYYY-MM-DDTHH:mm:ss") + "Z";
  };

  const updateStudentGuidanceItem = async (id, payload) => {
    const convertedDate = convertDateToISO(payload.completionDate);
    // console.log("Original completionDate:", payload.completionDate);
    // console.log("Converted completionDate:", convertedDate);

    let result = await props.updateStudentGuidance(id, {
      ...payload,
      completionDate: convertedDate,
      studentGuidanceProfile: profileList,
    });
    return result;
  };

  const checkForDuplicates = async (title, currentId) => {
    if (!title || title.trim() === "") {
      return false;
    }

    const result = await props.checkDuplicateStudentGuidance({
      publicationType: "student_guidances",
      title: title.trim(),
    });

    // console.log("Duplicate check result (Update):", result);
    if (result.success && Array.isArray(result.data) && result.data.length > 0) {
      const filteredList = result.data.filter((item) => item.studentGuidance?.id !== currentId);
      // console.log("Filtered duplicate list:", filteredList);
      if (filteredList.length > 0) {
        setDuplicateList(filteredList);
        setDuplicateModalOpen(true);
        return true;
      }
    }
    return false;
  };

  const handleSelectDuplicate = (item) => {
    setDuplicateModalOpen(false);
    setUpdating({
      ...item,
      studentGuidanceProfile: item.studentGuidanceProfile || [],
      _fromDuplicate: true,
    });
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateModalOpen(false);
    // Don't auto-submit when closing duplicate modal - user wants to cancel/review
  };

  const proceedWithSubmit = async () => {
    const result = await updateStudentGuidanceItem(values?.id, values);
    if (result && result.success) {
      // Call handleReload if available to refresh parent component data
      if (props.handleReload) {
        props.handleReload();
      }
      handleClose();
    }
  };

  const handleSubmit = async () => {
    // Check required fields
    if (!values?.title || !values?.completionDate || !values?.numberInstructor) {
      setHelperText(true);
      return;
    }

    // Check timeResearchDetailCode if purposeId is 5
    if (values?.purposeId === 5 && !values?.timeResearchDetailCode) {
      setHelperText(true);
      return;
    }

    // Validate profileList for external members
    const hasEmptyExternalName = profileList.some((e) => !e.isInternal && e.externalName === "");
    if (hasEmptyExternalName) {
      setHelperText(true);
      return;
    }

    // Validate at least one internal member
    const hasInternalMember = profileList.some((e) => e.isInternal === true);
    if (!hasInternalMember) {
      props.pushNoti({
        type: "warning",
        message: "Phải có ít nhất 1 thành viên nội bộ của trường trong công trình khoa học!",
      });
      return;
    }

    // Check for duplicates only when not in disabled mode (i.e., in edit mode)
    // if (!disabled) {
    //   const hasDuplicates = await checkForDuplicates(values?.title, values?.id);
    //   if (hasDuplicates) {
    //     return;
    //   }
    // }

    let finalFileUrl = fileUrls.join("||");
    if (newFiles.length > 0) {
      const uploadResult = await props.uploadMultiFile(values.id, newFiles, "studentGuidance");
      if (uploadResult.success) finalFileUrl = [...fileUrls, ...uploadResult.data].join("||");
    }
    const updateResult = await updateStudentGuidanceItem(values.id, { ...values, fileUrl: finalFileUrl });

    if (updateResult && updateResult.success) {
      props.statisticUnaccepted({ departmentId: 0, profileId: props.profile?.id });
      if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });

      // Call handleReload if available to refresh parent component data
      if (props.handleReload) {
        props.handleReload();
      }
    }

    setUpdating(null);
    setDisabled(true);
    setHelperText(false);
  };

  const handleOpenUploadModal = () => {};
  const handleCloseUploadModal = () => {};
  const handleOpenPreviewModal = () => {};
  const handleClosePreviewModal = () => {};

  const handleSendFeedback = () => {
    props.history.push({
      pathname: ROUTER.NOTIFICATION,
      state: {
        sendTo: "personal",
        title: "Phản hồi về việc khai báo thông tin Hướng dẫn sinh viên",
        chosenProfiles: profileList.reduce((accumulator, currentValue) => {
          accumulator.push(currentValue.profileId);
          return accumulator;
        }, []),
        data: updating,
        dataType: "studentGuidance",
      },
    });
  };

  return (
    <Dialog fullWidth keepMounted maxWidth="lg" open={updating !== null} onClose={handleClose} disableBackdropClick>
      <DialogTitle>Chi tiết hướng dẫn sinh viên</DialogTitle>
      <DialogContent style={{ display: "flex", gap: 16, overflowX: "hidden" }}>
        <div style={{ flex: "1 1 auto", minWidth: 0, paddingRight: 16 }}>
          <Grid container justifyContent="flex-start" spacing={2} style={{ marginBottom: 10 }}>
            <Grid item xs={10}>
              <TextField
                fullWidth
                size="small"
                label="Tên đề tài"
                variant="outlined"
                value={values?.title?.toString() || ""}
                required
                disabled={disabled}
                error={helperText && !values?.title}
                helperText={helperText && !values?.title ? "Vui lòng nhập tên đề tài" : ""}
                onChange={(event) => handleChangeForm("title", event.target.value)}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Mã CTKH"
                value={values?.hemisCode || ""}
                disabled={disabled}
                onChange={(event) => handleChangeForm("hemisCode", event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <KPTimePicker
                required
                fullWidth
                size="small"
                label="Ngày hoàn thành"
                disabled={disabled}
                value={values?.completionDate || "dd/mm/yyyy"}
                setValue={(newValue) => handleChangeForm("completionDate", newValue)}
              />
              {helperText && !values?.completionDate && (
                <FormHelperText error>Vui lòng chọn ngày hoàn thành</FormHelperText>
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Năm tính giờ"
                variant="outlined"
                value={values?.year ? `${values.year} - ${values.year + 1}` : ""}
                disabled
                InputProps={{
                  readOnly: true,
                }}
                helperText="Tự động tính từ ngày hoàn thành"
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                size="small"
                disabled={disabled}
                options={(props.departmentList || []).filter((d) => d.id !== 0)}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  (props.departmentList || []).filter((d) => d.id !== 0).find((d) => d.id === values?.departmentId) ||
                  null
                }
                onChange={(event, newValue) => handleChangeForm("departmentId", newValue?.id)}
                renderInput={(params) => <TextField {...params} size="small" label="Đơn vị" variant="outlined" />}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                variant="outlined"
                label="Số lượng GVHD"
                value={values?.numberInstructor || 1}
                required
                disabled={disabled}
                error={helperText && !values?.numberInstructor}
                helperText={helperText && !values?.numberInstructor ? "Vui lòng nhập số lượng GVHD" : ""}
                onChange={(event) => handleChangeForm("numberInstructor", parseInt(event.target.value))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                // label="Nhiệm vụ tính giờ nghiên cứu"
                variant="outlined"
                value={values?.timeResearchDetailCode || ""}
                disabled={disabled || !values?.year || timeResearchDetailList.length === 0}
                onChange={(event) => handleChangeForm("timeResearchDetailCode", event.target.value)}
                error={values?.purposeId === 5 && !values?.timeResearchDetailCode && helperText}
                helperText={
                  timeResearchDetailList.length === 0
                    ? `Chưa có dữ liệu cho năm ${values?.year ? `${values.year} - ${values.year + 1}` : ""}`
                    : values?.purposeId === 5 && !values?.timeResearchDetailCode && helperText
                      ? "Phải chọn một nhiệm vụ!"
                      : ""
                }
              >
                <MenuItem value="">-- Chọn nhiệm vụ --</MenuItem>
                {timeResearchDetailList.length > 0 ? (
                  timeResearchDetailList?.map((e) => (
                    <MenuItem key={e.code} value={e.code}>{`${e.description} - ${e.value}(h)`}</MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    Chưa có dữ liệu
                  </MenuItem>
                )}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Ghi chú"
                variant="outlined"
                value={values?.note || ""}
                disabled={disabled}
                multiline
                rows={3}
                onChange={(event) => handleChangeForm("note", event.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values?.isOvertimeCountable || false}
                    disabled={disabled || props.role !== "sudo-admin"}
                    onChange={(event) => handleChangeForm("isOvertimeCountable", event.target.checked)}
                    color="primary"
                  />
                }
                label="Tính giờ ngoài quy chế"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Số giờ ngoài quy chế"
                variant="outlined"
                value={values?.overtimeHours ?? ""}
                disabled={disabled || props.role !== "sudo-admin" || !values?.isOvertimeCountable}
                onChange={(event) => handleChangeForm("overtimeHours", parseFloat(event.target.value) || 0)}
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>

            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>
            <Grid item xs={2}>
              <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>Người hướng dẫn</Typography>
            </Grid>
            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>

            <Grid item xs={12}>
              {profileList.map((e, index) => (
                <Grid key={`list-${index}`} container spacing={2} style={{ marginBottom: 8 }}>
                  <Grid item xs={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={e.isInternal !== false}
                          disabled={disabled}
                          onChange={(event) => handleChangeProfileList(index, "isInternal", event.target.checked)}
                          color="primary"
                        />
                      }
                      label="Nội bộ"
                    />
                  </Grid>
                  {e.isInternal && (
                    <Grid item xs={5}>
                      <Autocomplete
                        disableClearable
                        disabled={disabled}
                        options={props.profileList}
                        getOptionLabel={(option) => option.name}
                        value={profileListFromProps?.find((profile) => profile.id === e.profileId)}
                        onChange={(event, profile) => {
                          handleChangeProfileList(index, "profileId", parseInt(profile?.id));
                        }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" label="Người hướng dẫn" variant="outlined" />
                        )}
                      />
                    </Grid>
                  )}
                  {!e.isInternal && (
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Tên người hướng dẫn"
                        value={e?.externalName || ""}
                        disabled={disabled}
                        onChange={(event) => handleChangeProfileList(index, "externalName", event.target.value)}
                      />
                      {helperText && e.externalName === "" && (
                        <FormHelperText style={{ color: "red" }}>Không được để trống</FormHelperText>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={5}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel
                        id="roleInStudentGuidance-label"
                        style={{ padding: "0 8px", backgroundColor: "white" }}
                      >
                        Vai trò
                      </InputLabel>
                      <Select
                        native
                        fullWidth
                        disabled={disabled}
                        labelId="roleInStudentGuidance-label"
                        value={e.roleInStudentGuidance}
                        onChange={(event) =>
                          handleChangeProfileList(index, "roleInStudentGuidance", event.target.value)
                        }
                      >
                        {STUDENT_GUIDANCE.roleOptions.map((role) => (
                          <option key={role.key} value={role.value}>
                            {role.title}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      disabled={disabled}
                      style={{ height: 40 }}
                      onClick={() => handleRemove(index)}
                    >
                      Xoá
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            {!disabled && (
              <Grid item xs={12}>
                <Button fullWidth variant="outlined" onClick={handleInsert}>
                  Thêm người hướng dẫn
                </Button>
              </Grid>
            )}
          </Grid>
        </div>
        <div style={{ flex: "0 0 380px", paddingLeft: 16, borderLeft: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
            File minh chứng
          </Typography>
          <MultiFileManager
            fileUrls={fileUrls}
            setFileUrls={setFileUrls}
            newFiles={newFiles}
            setNewFiles={setNewFiles}
            disabled={disabled}
            key={fileManagerKey}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ padding: "0 24px 8px 24px" }}>
        <Grid container justifyContent={"space-between"}>
          <div />
          <div style={{ float: "right" }}>
            {props.adminTab && (
              <Button variant="outlined" onClick={handleSendFeedback} style={{ marginRight: 4 }}>
                Gửi feedback
              </Button>
            )}
            {disabled ? (
              <>
                {(props.adminTab !== "all" || props.role === "sudo-admin") && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setDisabled(false)}
                    style={{ marginRight: 4 }}
                  >
                    Chỉnh sửa
                  </Button>
                )}
                <Button variant="outlined" color="secondary" onClick={handleClose}>
                  Đóng
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" color="secondary" onClick={handleClose} style={{ marginRight: 4 }}>
                  Huỷ
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  Xác nhận
                </Button>
              </>
            )}
          </div>
        </Grid>
      </DialogActions>
      <DuplicateCheckModal
        open={duplicateModalOpen}
        handleClose={handleCloseDuplicateModal}
        handleSelect={handleSelectDuplicate}
        duplicateList={duplicateList}
      />
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  history: select(state, "homeReducer", "history"),
  profile: select(state, "profileReducer", "detail"),
  profileList: select(state, "profileReducer", "list"),
  departmentList: select(state, "departmentReducer", "departments"),
  timeResearchInformationList: select(state, "timeResearchInformationReducer", "list"),
});

const mapDispatchToProps = (dispatch) => ({
  statisticUnaccepted: (payload) => dispatch(statisticUnaccepted(payload)),
  updateStudentGuidance: (id, payload) => dispatch(updateStudentGuidance(id, payload)),
  uploadMultiFile: (id, files, type) => dispatch(uploadMultiFile(id, files, type)),
  checkDuplicateStudentGuidance: (payload) => dispatch(checkDuplicateStudentGuidance(payload)),
  pushNoti: (payload) => dispatch(notification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(StudentGuidanceUpdateModal));

